import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Close from "@mui/icons-material/Close";
import { Dialog } from "components/Dialog";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { PriorityOrderSwitch } from "components/Submission/PriorityOrderSwitch";
import { OmnisearchAutocomplete } from "components/OmnisearchAutocomplete";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  ReviewMode,
  SubmissionReviewerPickerQuery,
  StaffReviewerPickerQuery,
} from "generated-graphql/graphql";
import { gql } from "generated-graphql";
import { DateTime } from "luxon";
import { useMemo, useState, useEffect } from "react";
import { BULK_ADD_REVIEWERS_MUTATION } from "queries/submissions";
import { useCurrentUser } from "hooks/useCurrentUser";
import { v4 as uuidv4 } from "uuid";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import {
  STATUS_LABELS,
  isSubmissionLockedForBulkAction,
} from "utils/submissionStatusLabels";
import {
  formatDateLikeForDisplay,
  toUtcMidnightIsoFromDateLike,
} from "util/dates";
import { BulkActionSubmission } from "./types";

// Simplified reviewer type for bulk operations
interface BulkReviewer {
  id: string;
  name: string;
  email: string;
  dueDate?: DateTime;
  isStaff: boolean;
  order: number;
}

const SUBMISSION_REVIEWER_PICKER_QUERY = gql(`
  query SubmissionReviewerPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...SubmissionReviewerPickerUser
      }
    }
  }
`);

const STAFF_REVIEWER_PICKER_QUERY = gql(`
  query StaffReviewerPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users: staffUsers(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...SubmissionReviewerPickerUser
      }
    }
  }
`);

type BulkAddReviewersDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: BulkActionSubmission[];
  onSuccess: () => void;
  /** GraphQL queries to refetch after mutation */
  refetchQueries?: string[];
};

export function BulkAddReviewersDialog({
  open,
  onClose,
  submissions,
  onSuccess,
  refetchQueries = [],
}: BulkAddReviewersDialogProps) {
  const { isStaff } = useCurrentUser();
  const alerts = useAlerts();

  // Reviewer state
  const [reviewers, setReviewers] = useState<BulkReviewer[]>([
    { id: uuidv4(), name: "", email: "", order: 1, isStaff: false },
  ]);
  const [staffReviewer, setStaffReviewer] = useState<BulkReviewer | null>(null);
  const [reviewMode, setReviewMode] = useState<ReviewMode>(ReviewMode.Parallel);
  const [clearExistingReviewers, setClearExistingReviewers] = useState(false);

  const [bulkAddReviewers, { loading }] = useMutation(
    BULK_ADD_REVIEWERS_MUTATION,
    {
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    }
  );

  // Partition virtual events from real submissions and filter out locked statuses
  const {
    virtualEvents,
    lockedSubmissions,
    eligibleSubmissions,
    eligibleSubmissionIds,
  } = useMemo(() => {
    const virtualEvents: BulkActionSubmission[] = [];
    const materialized: BulkActionSubmission[] = [];

    for (const row of submissions) {
      if (row.isVirtualEvent) {
        virtualEvents.push(row);
      } else {
        materialized.push(row);
      }
    }

    // Separate locked from eligible
    const lockedSubmissions = materialized.filter((s) =>
      isSubmissionLockedForBulkAction(s.status)
    );
    const eligibleSubmissions = materialized.filter(
      (s) => !isSubmissionLockedForBulkAction(s.status)
    );
    const eligibleSubmissionIds = eligibleSubmissions
      .map((s) => s.submissionId)
      .filter((id): id is string => Boolean(id));

    return {
      virtualEvents,
      lockedSubmissions,
      eligibleSubmissions,
      eligibleSubmissionIds,
    };
  }, [submissions]);

  const handleClose = () => {
    // Reset state
    setReviewers([
      { id: uuidv4(), name: "", email: "", order: 1, isStaff: false },
    ]);
    setStaffReviewer(null);
    setReviewMode(ReviewMode.Parallel);
    setClearExistingReviewers(false);
    onClose();
  };

  const handleSubmit = async () => {
    // Build valid reviewers list
    const validReviewers = reviewers.filter((r) => r.name.trim());

    // Require at least one reviewer unless clearing existing reviewers
    if (
      validReviewers.length === 0 &&
      !staffReviewer?.email?.trim() &&
      !clearExistingReviewers
    ) {
      alerts.error("Please add at least one reviewer");
      return;
    }

    if (eligibleSubmissionIds.length === 0) {
      alerts.error(
        "No eligible submissions selected. Virtual events must be started and submissions must not be in review or complete."
      );
      return;
    }

    // Build reviewer input list
    const reviewerInputs = [
      ...(staffReviewer?.email?.trim()
        ? [
            {
              userId: staffReviewer.id,
              reviewOrder: 0,
              isStaff: true,
              isRequired: true,
              isSigner: false,
              dueDate: staffReviewer.dueDate
                ? toUtcMidnightIsoFromDateLike(staffReviewer.dueDate) ?? null
                : null,
            },
          ]
        : []),
      ...validReviewers.map((r, index) => ({
        userId: r.id,
        reviewOrder: index + 1,
        isStaff: false,
        isRequired: true,
        isSigner: false,
        dueDate: r.dueDate
          ? toUtcMidnightIsoFromDateLike(r.dueDate) ?? null
          : null,
      })),
    ];

    try {
      const result = await bulkAddReviewers({
        variables: {
          input: {
            submissionIds: eligibleSubmissionIds,
            reviewers: reviewerInputs,
            reviewMode,
            clearExistingReviewers,
          },
        },
      });

      if (result.data?.bulkAddReviewers.succeeded) {
        const skippedCount = virtualEvents.length + lockedSubmissions.length;
        alerts.success(
          `Successfully added reviewers to ${result.data.bulkAddReviewers.updated} submission(s)` +
            (skippedCount > 0 ? `. ${skippedCount} submission(s) skipped.` : "")
        );
        handleClose();
        onSuccess();
      } else {
        alerts.error("Failed to add reviewers");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      alerts.error(`Failed to add reviewers: ${message}`);
    }
  };

  const tableRows = useMemo(() => {
    // Eligible submissions
    const eligibleRows = eligibleSubmissions.map((s) => ({
      id: s.id,
      isVirtual: false,
      isLocked: false,
      dueDate: s.dueDate,
      facility: s.facilityName,
      submissionType: s.submissionType,
      title: s.title,
      status: s.status,
    }));

    // Locked submissions (will be skipped)
    const lockedRows = lockedSubmissions.map((s) => ({
      id: s.id,
      isVirtual: false,
      isLocked: true,
      dueDate: s.dueDate,
      facility: s.facilityName,
      submissionType: s.submissionType,
      title: s.title,
      status: s.status,
    }));

    // Virtual events (will be skipped)
    const virtualRows = virtualEvents.map((s) => ({
      id: s.id,
      isVirtual: true,
      isLocked: false,
      dueDate: s.dueDate,
      facility: s.facilityName,
      submissionType: s.submissionType,
      title: s.title,
      status: null as string | null,
    }));

    return [...eligibleRows, ...lockedRows, ...virtualRows];
  }, [eligibleSubmissions, lockedSubmissions, virtualEvents]);

  const columns: CustomGridColDef<(typeof tableRows)[number]>[] = [
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 100,
      valueGetter: (value, row) =>
        row.dueDate ? formatDateLikeForDisplay(row.dueDate) : "",
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "submissionType",
      headerName: "Submission Type",
      width: 130,
      valueGetter: (value, row) => prettyPrintEnumValue(row.submissionType),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      valueGetter: (value, row) => {
        if (row.isVirtual) return "Virtual (skipped)";
        if (row.isLocked && row.status) {
          return `${
            STATUS_LABELS[row.status] ?? prettyPrintEnumValue(row.status)
          } (skipped)`;
        }
        if (row.status) {
          return STATUS_LABELS[row.status] ?? prettyPrintEnumValue(row.status);
        }
        return "";
      },
    },
  ];

  const validReviewerCount =
    reviewers.filter((r) => r.name.trim()).length +
    (staffReviewer?.email?.trim() ? 1 : 0);
  const canSubmit =
    (validReviewerCount > 0 || clearExistingReviewers) &&
    eligibleSubmissionIds.length > 0;
  const skippedCount = virtualEvents.length + lockedSubmissions.length;

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        title={`Add reviewer(s) to ${submissions.length} submissions`}
        description={
          <>
            Add reviewers to the selected submissions. These reviewers will be
            added after any existing reviewers unless cleared.
          </>
        }
        primaryActionButtonProps={{
          saveText: loading ? "SAVING..." : "SAVE",
          onClick: handleSubmit,
          disabled: loading || !canSubmit,
        }}
        cancelButtonProps={{
          text: "CANCEL",
          onClick: handleClose,
        }}
      >
        <Stack spacing={3}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PriorityOrderSwitch
              value={reviewMode}
              onChange={setReviewMode}
              labelSize="16px"
            />
          </Box>

          {/* Staff Reviewer */}
          <Box sx={!isStaff ? { display: "none" } : {}}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body1" width="163px" alignContent="center">
                Staff Reviewer
              </Typography>
              <BulkReviewerPicker
                staffOnly={true}
                onChange={setStaffReviewer}
                value={staffReviewer}
              />
            </Box>
            <Divider sx={{ mt: 3 }} />
          </Box>

          {/* Customer Reviewers List */}
          <BulkReviewersEditList
            reviewers={reviewers}
            setReviewers={setReviewers}
            rowReordering={reviewMode === ReviewMode.Sequential}
          />

          {/* Preview Table with Clear Existing Reviewers Checkbox */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2">
              Submissions ({submissions.length} selected)
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clearExistingReviewers}
                  onChange={(e) => setClearExistingReviewers(e.target.checked)}
                />
              }
              label="Clear existing reviewers"
            />
          </Box>
          <DataGrid
            rows={tableRows}
            columns={columns}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={[5, 10]}
            hideFooterPagination={false}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            sx={{ maxHeight: 300 }}
          />

          {skippedCount > 0 && (
            <Alert severity="info">
              {virtualEvents.length > 0 &&
                `${virtualEvents.length} virtual event(s) must be started before reviewers can be added. `}
              {lockedSubmissions.length > 0 &&
                `${lockedSubmissions.length} submission(s) are in a locked status and will be skipped.`}
            </Alert>
          )}
        </Stack>
      </Dialog>
    </LocalizationProvider>
  );
}

// Standalone reviewer picker
interface BulkReviewerPickerProps {
  onChange: (reviewer: BulkReviewer | null) => void;
  value?: BulkReviewer | null;
  disabled?: boolean;
  disabledReviewers?: BulkReviewer[];
  staffOnly?: boolean;
}

function BulkReviewerPicker({
  onChange,
  value,
  disabled,
  disabledReviewers,
  staffOnly = false,
}: BulkReviewerPickerProps) {
  if (staffOnly) {
    return (
      <OmnisearchAutocomplete<StaffReviewerPickerQuery, any, BulkReviewer>
        dataQuery={STAFF_REVIEWER_PICKER_QUERY}
        getItems={(data) =>
          data?.users?.items.map(
            (
              r: StaffReviewerPickerQuery["users"]["items"][number],
              index: number
            ) => ({
              id: r.id,
              name: r.person ? getFullName(r.person) : r.email,
              email: r.email,
              order: index + 1,
              isStaff: true,
            })
          ) ?? []
        }
        getOptionLabel={(item) => {
          return item?.name !== item?.email
            ? `${item?.name} (${item?.email})`
            : item?.email;
        }}
        onChange={onChange}
        value={value || null}
        disabled={disabled}
        runInitialSearch={true}
        getOptionDisabled={(item) => {
          return disabledReviewers?.some((r) => r.id === item?.id) ?? false;
        }}
        sx={{ width: "100%" }}
        hideError={true}
      />
    );
  }

  return (
    <OmnisearchAutocomplete<SubmissionReviewerPickerQuery, any, BulkReviewer>
      dataQuery={SUBMISSION_REVIEWER_PICKER_QUERY}
      getItems={(data) =>
        data?.users?.items.map(
          (
            r: SubmissionReviewerPickerQuery["users"]["items"][number],
            index: number
          ) => ({
            id: r.id,
            name: r.person ? getFullName(r.person) : r.email,
            email: r.email,
            order: index + 1,
            isStaff: false,
          })
        ) ?? []
      }
      getOptionLabel={(item) => {
        return item?.name !== item?.email
          ? `${item?.name} (${item?.email})`
          : item?.email;
      }}
      onChange={onChange}
      value={value || null}
      disabled={disabled}
      runInitialSearch={true}
      getOptionDisabled={(item) => {
        return disabledReviewers?.some((r) => r.id === item?.id) ?? false;
      }}
      sx={{ width: "100%" }}
      hideError={true}
    />
  );
}

// Standalone reviewers list
interface BulkReviewersEditListProps {
  reviewers: BulkReviewer[];
  setReviewers: (reviewers: BulkReviewer[]) => void;
  rowReordering?: boolean;
}

function BulkReviewersEditList({
  reviewers,
  setReviewers,
  rowReordering,
}: BulkReviewersEditListProps) {
  const [reviewerList, setReviewerList] = useState<BulkReviewer[]>(
    [...reviewers]
      .sort((a, b) => a.order - b.order)
      .map((r) => ({
        ...r,
        __reorder__: r.name ?? "",
      }))
  );

  const updateReviewer = (
    reviewerId: string,
    updates: Partial<BulkReviewer>
  ) => {
    setReviewerList((prev) =>
      prev.map((r) => (r.id === reviewerId ? { ...r, ...updates } : r))
    );
  };

  const handleAddReviewer = () => {
    const newReviewer: BulkReviewer = {
      id: uuidv4(),
      name: "",
      email: "",
      isStaff: false,
      order: reviewerList.length + 1,
    };
    setReviewerList([...reviewerList, newReviewer]);
  };

  useEffect(() => {
    setReviewers(reviewerList);
  }, [reviewerList, setReviewers]);

  const columns: GridColDef<BulkReviewer>[] = useMemo(
    () => [
      ...(rowReordering
        ? [
            {
              field: "reviewOrder",
              headerName: "",
              width: 60,
              renderCell: ({ row }: { row: BulkReviewer }) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      height: "100%",
                      pl: "20px",
                      py: "10px",
                    }}
                  >
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      {row.order}
                    </Typography>
                  </Box>
                );
              },
            } as GridColDef<BulkReviewer>,
          ]
        : []),
      {
        field: "content",
        headerName: "",
        align: "center",
        display: "flex",
        flex: 1,
        renderCell: ({ row }) => {
          return (
            <Box sx={{ py: "8px", px: 2, width: "100%" }}>
              <BulkReviewerPicker
                onChange={(selectedUser) => {
                  updateReviewer(row.id, {
                    id: selectedUser?.id ?? row.id,
                    email: selectedUser?.email ?? "",
                    name: selectedUser?.name ?? "",
                  });
                }}
                value={row.name ? row : null}
                disabledReviewers={reviewerList.filter(
                  (r) => r.id !== row.id && r.name
                )}
              />
            </Box>
          );
        },
      },
      {
        field: "dueDate",
        headerName: "",
        display: "flex",
        flex: 0.55,
        renderCell: ({ row }) => {
          return (
            <Box
              sx={{
                width: "100%",
                py: "8px",
                "& .MuiTextField-root": { margin: 0 },
              }}
            >
              <DatePicker
                label="Due date"
                value={row.dueDate ?? null}
                onChange={(date) => {
                  updateReviewer(row.id, {
                    dueDate: date ?? undefined,
                  });
                }}
                slotProps={{
                  textField: {
                    sx: {
                      width: "100%",
                      margin: 0,
                    },
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        display: "flex",
        flex: 0.15,
        renderCell: ({ row }) => {
          return (
            <Button
              variant="text"
              color="inherit"
              sx={{ mt: 1, minWidth: "auto", py: "16px", pl: "20px" }}
              onClick={() => {
                setReviewerList((prev) => {
                  const filtered = prev.filter((r) => r.id !== row.id);
                  if (filtered.length === 0) {
                    return [
                      {
                        id: uuidv4(),
                        name: "",
                        email: "",
                        order: 1,
                        isStaff: false,
                      },
                    ];
                  }
                  return filtered.map((r, index) => ({
                    ...r,
                    order: index + 1,
                  }));
                });
              }}
            >
              <Close />
            </Button>
          );
        },
      },
    ],
    [reviewerList, rowReordering]
  );

  return (
    <>
      <Box
        sx={{
          maxHeight: 375,
          overflowY: "auto",
          border: "none",
        }}
      >
        <DataGridPremium
          rows={reviewerList}
          columns={columns}
          rowReordering={rowReordering}
          onRowOrderChange={(params) => {
            setReviewerList((prev) => {
              const newList = [...prev];
              const oldIndex = params.oldIndex;
              const newIndex = params.targetIndex;
              const [movedItem] = newList.splice(oldIndex, 1);
              newList.splice(newIndex, 0, movedItem);
              newList.forEach((r, index) => {
                r.order = index + 1;
              });
              return newList;
            });
          }}
          hideFooter
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          getRowHeight={() => "auto"}
          autoHeight={true}
          sx={{
            border: "none",
            "& .MuiDataGrid-row": {
              maxHeight: "none",
            },
            "& .MuiDataGrid-cell": {
              maxHeight: "none",
              display: "flex",
              alignItems: "flex-start",
              paddingTop: 0,
              paddingBottom: 0,
            },
            "& .MuiDataGrid-rowReorderCell": {
              paddingTop: "26px",
            },
            "& .MuiDataGrid-main": {
              "& .MuiDataGrid-columnHeaders": {
                display: "none",
              },
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within":
              {
                outline: "none",
              },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "transparent",
            },
          }}
        />
      </Box>
      <Button
        startIcon={<PersonAdd />}
        onClick={handleAddReviewer}
        variant="text"
        color="primary"
        sx={{ alignSelf: "flex-start" }}
      >
        Additional reviewer
      </Button>
    </>
  );
}
